/**
 * Overdue-invoice **severity + copy** — the ONE definition of "how bad is this
 * debt", shared by every surface that warns about it:
 *
 *  - the CRM-wide warning bar (`apps/web/src/crm/lib/overdue-invoices-api.ts`,
 *    which re-exports these so its own public API is unchanged),
 *  - the dashboard server's summary endpoint
 *    (`apps/server/src/services/crmOverdue.ts`),
 *  - the top-nav CRM alert dot (`apps/web/src/components/OverdueNavAlert.tsx`).
 *
 * Money never appears here. These functions take **totals a caller already
 * holds** and return a severity / a sentence; whether the caller was allowed to
 * compute those totals is decided by the financial wall at the fetch site
 * (`crm:financials`), not by this module.
 */

/** An invoice this many days past due flips "warning" → "critical". A month
 * late is no longer a slip. */
export const CRITICAL_DAYS_OVERDUE = 30;

/** …as does having this many overdue at once: individually young debts still
 * add up to a collections problem. */
export const CRITICAL_INVOICE_COUNT = 5;

/** How loud the warning gets. `critical` earns the animated treatment. */
export type OverdueSeverity = "warning" | "critical";

/** The rolled-up shape every overdue surface renders from. */
export interface OverdueTotals {
  /** How many invoices are past due. The SERVER-reported total (which may
   * exceed the fetched page), so a headline never under-reports. */
  count: number;
  /** Sum still outstanding across them (balance due, else the full amount). */
  totalAmount: number;
  /** Days past due of the OLDEST one — what drives the severity. */
  maxDaysOverdue: number;
}

/** Severity for a NON-EMPTY summary. Callers render nothing at count 0. */
export function overdueSeverity(
  totals: Pick<OverdueTotals, "count" | "maxDaysOverdue">,
): OverdueSeverity {
  return totals.maxDaysOverdue >= CRITICAL_DAYS_OVERDUE ||
    totals.count >= CRITICAL_INVOICE_COUNT
    ? "critical"
    : "warning";
}

/** "1 invoice is overdue" / "6 invoices are overdue" — the headline clause. */
export function overdueCountPhrase(count: number): string {
  return `${count} ${count === 1 ? "invoice is" : "invoices are"} overdue`;
}

/** "the oldest is 82 days past due", or `null` when nothing has aged yet
 * (same-day / clock-skew edge — the server's filter, not ours, decides what
 * counts as overdue, so 0 days is possible). */
export function overdueOldestPhrase(maxDaysOverdue: number): string | null {
  if (maxDaysOverdue <= 0) return null;
  return `the oldest is ${maxDaysOverdue} day${maxDaysOverdue === 1 ? "" : "s"} past due`;
}
